/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/core";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import { useI18n } from "hooks/i18n/i18n";
import React, { useState } from "react";
import { useSelector } from "store";

import { StreamingLiveCommerceAddressV2 } from "./StreamingLiveCommerceAddressV2";
import { StreamingLiveCommerceAmountV2 } from "./StreamingLiveCommerceAmountV2";
import { StreamingLiveCommerceCartLayoutV2 } from "./StreamingLiveCommerceCartLayoutV2";
import { StreamingLiveCommerceItemListV2 } from "./StreamingLiveCommerceItemListV2";

import type { PayType } from "@spwn/types";
import type { ProductData } from "@spwn/types/firebase/firestore";
import type { AddressInfo } from "modules/auth";
import type { Store } from "store";
import type { EventInfo } from "utility";

type Props = {
  playerHeight: number;
  addressInfo: AddressInfo | null;
  paymentMethod: PayType;
  pseudoCart: (ProductData & { count: number })[];
  displayEvent: EventInfo;
  cartTotalFee: number;
  shippingFee: number;
  settlementFee: number;
  totalFee: number;
  handleBackToCart: () => void;
  handlePurchase: () => void;
};

const StreamingLiveCommerceCartConfirmV2: React.FC<Props> = (props) => {
  const isDarkModeEnabled = useSelector(
    (state: Store) => state.streaming.streamingSettings.isDarkModeEnabled
  );
  const [isAgreed, setIsAgreed] = useState(false);

  const { t } = useI18n();
  const paymentMethodValues: { [key: string]: string } = {
    Card: t("streaming.commerceCart.paymentCard"),
    CVS: t("streaming.commerceCart.paymentCvs"),
    Phone: t("streaming.commerceCart.paymentPhone"),
  };
  const paymentMethodValue = paymentMethodValues[props.paymentMethod] ?? "";
  const classesLiveCommerceConfirmDesign = liveCommerceCartConfirmDesign({
    isDarkModeEnabled,
    playerHeight: props.playerHeight,
    isAgreed,
  });
  const headerLeft = <ArrowBackIosIcon onClick={props.handleBackToCart} />;
  const headerCenter = (
    <p>
      {t("streaming.commerceCart.total")} : ¥{props.totalFee.toLocaleString()}
    </p>
  );
  const headerRight = (
    <div
      css={classesLiveCommerceConfirmDesign.purchaseButton}
      onClick={props.handlePurchase}
      style={{
        pointerEvents: isAgreed ? "auto" : "none",
        opacity: isAgreed ? 1 : 0.5,
      }}
    >
      {t("streaming.commerceCart.purchase")}
    </div>
  );
  const addressSection = (
    <StreamingLiveCommerceAddressV2 addressInfo={props.addressInfo} />
  );
  const paymentSection = (
    <div>
      <p css={classesLiveCommerceConfirmDesign.paymentMethodLabel}>
        {t("streaming.commerceCart.payment")}
      </p>
      <p css={classesLiveCommerceConfirmDesign.paymentMethodValue}>
        {paymentMethodValue}
      </p>
    </div>
  );

  const cartLabel = t("streaming.commerceCart.order");
  const itemListSection = (
    <StreamingLiveCommerceItemListV2
      pseudoCart={props.pseudoCart}
      THEOPlayerElementHeight={props.playerHeight}
      isDarkModeEnabled={isDarkModeEnabled}
      displayEvent={props.displayEvent}
    />
  );
  const summarySection = (
    <StreamingLiveCommerceAmountV2
      THEOPlayerElementHeight={props.playerHeight}
      isDarkModeEnabled={isDarkModeEnabled}
      cartTotalFee={props.cartTotalFee}
      shippingFee={props.shippingFee}
      settlementFee={props.settlementFee}
      totalFee={props.totalFee}
      isCalculatingShippingFee={false}
    />
  );

  const termsOfServiceSection = (
    <div>
      <h2 css={classesLiveCommerceConfirmDesign.termsOfServiceTitle}>
        {t("streaming.commerceCart.termsOfSpwn")}
      </h2>
      <a
        href="https://spwn.jp/terms-of-service"
        target="_blank"
        rel="noopener noreferrer"
        css={classesLiveCommerceConfirmDesign.termsOfServiceBox}
      >
        {t("streaming.commerceCart.termsOfSpwn")}
      </a>

      <label css={classesLiveCommerceConfirmDesign.agreementSection}>
        <input
          type="checkbox"
          checked={isAgreed}
          onChange={() => setIsAgreed(!isAgreed)}
        />
        <span>{t("streaming.commerceCart.agreeToTerms")}</span>
      </label>
    </div>
  );

  return (
    <StreamingLiveCommerceCartLayoutV2
      isDarkMode={isDarkModeEnabled}
      playerHeight={props.playerHeight}
      headerLeft={headerLeft}
      headerCenter={headerCenter}
      headerRight={headerRight}
      addressSection={addressSection}
      paymentSection={paymentSection}
      cartLabel={cartLabel}
      itemListSection={itemListSection}
      summarySection={summarySection}
      termsOfServiceSection={termsOfServiceSection}
    />
  );
};

type LiveCommerceCartConfirmDesignProps = {
  playerHeight?: number;
  isDarkModeEnabled: boolean;
  isAgreed: boolean;
};
const liveCommerceCartConfirmDesign = (
  props?: LiveCommerceCartConfirmDesignProps
) => {
  const textInDarkMode = props?.isDarkModeEnabled
    ? css`
        color: #fff;
      `
    : null;
  const darkModeBorder = props?.isDarkModeEnabled
    ? "none"
    : "1px solid #000000";
  const purchaseButton = css`
    padding: 6px 12px;
    background-color: ${props?.isDarkModeEnabled ? "#000000" : "#ffffff"};
    color: ${textInDarkMode};
    border: ${darkModeBorder};
    font-weight: bold;
    border-radius: 4px;
    font-size: 14px;
    cursor: pointer;
    /* 非活性時のスタイル */
    ${!props?.isAgreed &&
    css`
      opacity: 0.5;
      cursor: not-allowed;
      pointer-events: none; /* これでクリックを物理的に無効化 */
    `}
  `;
  const paymentMethodLabel = css`
    ${textInDarkMode}
    margin-bottom: 8px;
    font-size: 14px;
  `;
  const paymentMethodValue = css`
    ${textInDarkMode}
    font-size: 15px;
  `;

  const termsOfServiceTitle = css`
    ${textInDarkMode}
    font-size: 14px;
    font-weight: bold;
    margin-bottom: 16px;
    text-align: center;
  `;

  const termsOfServiceBox = css`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 210px; /* 画像の比率に合わせ調整 */
    background-color: #ffffff;
    color: ${textInDarkMode};
    font-size: 24px;
    font-weight: bold;
    text-decoration: none;
    margin-bottom: 24px;
    cursor: pointer;
    border: ${darkModeBorder};
    &:hover {
      opacity: 0.9;
    }
  `;

  const agreementSection = css`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 12px;
    ${textInDarkMode}
    font-size: 14px;
    cursor: pointer;
    input[type="checkbox"] {
      cursor: pointer;
      width: 20px;
      height: 20px;
      accent-color: ${textInDarkMode}; /* チェック時の色 */
    }
  `;

  return {
    purchaseButton,
    paymentMethodLabel,
    paymentMethodValue,
    termsOfServiceTitle,
    termsOfServiceBox,
    agreementSection,
  };
};

export default StreamingLiveCommerceCartConfirmV2;
